/**
 * Netxol Admin — the panel Netxol's own staff use to run one.netxol.com (admin.one.netxol.com).
 * Same look and components as the console; its own sign-in (password + two-factor), its own session and API.
 */
import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Server, Headphones, Landmark, SlidersHorizontal, Wallet, Sparkles, BellRing, Building2, FileText, Smartphone, Check, MessageSquare, ChevronDown, Globe, KeyRound, LayoutDashboard, Loader2, LogOut, Mail, Moon, ScrollText, Settings, ShieldCheck, Sun, Users } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '@/shared/api/client';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { FormDialog } from '@/shared/components/data/FormDialog';
import { NetxolLogo, NetxolMark } from '@/shared/components/shell/NetxolLogo';
import { versionLabel } from '@/shared/lib/version';
import { cn } from '@/shared/lib/utils';
import { LANGS, setLanguage, useT, type Lang } from '@/i18n';
import { can, plat, useAdmin, type Staff } from './api';
import { lazyPages } from '@/app/lazy';

// each page is its own chunk: charts (ECharts) and the export code load only with the pages that use them
const { DashboardPage } = lazyPages(() => import('./pages/Dashboard'));
const { IspDetailPage, IspsPage } = lazyPages(() => import('./pages/Isps'));
const { StaffPage } = lazyPages(() => import('./pages/Staff'));
const { AuditPage } = lazyPages(() => import('./pages/Audit'));
const { MailPage } = lazyPages(() => import('./pages/Mail'));
const { SettingsPage } = lazyPages(() => import('./pages/Settings'));
const { CompliancePage } = lazyPages(() => import('./pages/Compliance'));
const { MessagingAdminPage } = lazyPages(() => import('./pages/Messaging'));
const { PushPage } = lazyPages(() => import('./pages/Push'));
const { MobileAppsPage } = lazyPages(() => import('./pages/MobileApps'));
const { LogsPage } = lazyPages(() => import('./pages/Logs'));
const { AiOutcomesAdminPage } = lazyPages(() => import('./pages/AiOutcomes'));
const { PaymentDeskPage, SupportDeskPage } = lazyPages(() => import('./pages/Desk'));
const { DeskFinancePage } = lazyPages(() => import('./pages/DeskFinance'));
const { DeskIspsPage } = lazyPages(() => import('./pages/DeskIsps'));
const { InstallationDetailPage, InstallationsPage } = lazyPages(() => import('./pages/Installations'));

const NAV: { to: string; key: string; icon: typeof LayoutDashboard; caps?: string[]; end?: boolean }[] = [
  { to: '/', key: 'dashboard', icon: LayoutDashboard, caps: ['isps.view'], end: true },
  { to: '/isps', key: 'isps', icon: Building2, caps: ['isps.view'] },
  { to: '/installations', key: 'installations', icon: Server, caps: ['isps.view'] },
  { to: '/desk', key: 'desk', icon: Wallet, caps: ['desk.agent', 'desk.finance', 'desk.admin'] },
  { to: '/desk-support', key: 'desk_support', icon: Headphones, caps: ['desk.agent', 'desk.finance', 'desk.admin'] },
  { to: '/desk-finance', key: 'desk_finance', icon: Landmark, caps: ['desk.finance', 'desk.agent', 'desk.admin'] },
  { to: '/desk-isps', key: 'desk_isps', icon: SlidersHorizontal, caps: ['desk.admin', 'desk.finance', 'desk.agent'] },
  { to: '/staff', key: 'staff', icon: Users, caps: ['admins.manage'] },
  { to: '/mail', key: 'mail', icon: Mail, caps: ['isps.view'] },
  { to: '/logs', key: 'logs', icon: FileText, caps: ['isps.view'] },
  { to: '/ai-outcomes', key: 'ai_outcomes', icon: Sparkles, caps: ['isps.view'] },
  { to: '/audit', key: 'audit', icon: ScrollText, caps: ['audit.view'] },
  { to: '/compliance', key: 'compliance', icon: ShieldCheck, caps: ['compliance.view', 'compliance.manage'] },
  { to: '/messaging', key: 'messaging', icon: MessageSquare, caps: ['messaging.view', 'messaging.manage'] },
  { to: '/push', key: 'push', icon: BellRing, caps: ['isps.view'] },
  { to: '/mobile-apps', key: 'mobile', icon: Smartphone, caps: ['isps.view'] },
  { to: '/settings', key: 'settings', icon: Settings, caps: ['isps.view'] },
];

export default function AdminApp() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireStaff><Shell /></RequireStaff>}>
        <Route index element={<DashboardPage />} />
        <Route path="isps" element={<IspsPage />} />
        <Route path="isps/:id" element={<IspDetailPage />} />
        <Route path="installations" element={<InstallationsPage />} />
        <Route path="installations/:id" element={<InstallationDetailPage />} />
        <Route path="desk" element={<PaymentDeskPage />} />
        <Route path="desk-support" element={<SupportDeskPage />} />
        <Route path="desk-finance" element={<DeskFinancePage />} />
        <Route path="desk-isps" element={<DeskIspsPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="mail" element={<MailPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="ai-outcomes" element={<AiOutcomesAdminPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="compliance" element={<CompliancePage view="evidence" />} />
        <Route path="compliance/controls" element={<CompliancePage view="controls" />} />
        <Route path="compliance/ai" element={<CompliancePage view="ai" />} />
        <Route path="messaging" element={<MessagingAdminPage />} />
        <Route path="push" element={<PushPage />} />
        <Route path="mobile-apps" element={<MobileAppsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequireStaff({ children }: { children: ReactNode }) {
  const token = useAdmin((s) => s.accessToken);
  const ready = useAdmin((s) => s.ready);
  const loc = useLocation();
  if (!ready) return <div className="flex h-screen items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  if (!token) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return <>{children}</>;
}

function Shell() {
  const t = useT();
  const user = useAdmin((s) => s.user);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => { document.documentElement.classList.toggle('dark', dark); localStorage.setItem('nx.theme', dark ? 'dark' : 'light'); }, [dark]);
  useEffect(() => { document.title = 'Netxol Admin'; }, []);
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="flex w-60 shrink-0 flex-col border-e bg-card/70 backdrop-blur">
        <div className="flex h-16 items-center gap-2 px-4"><NetxolLogo /><span className="rounded-md bg-gradient-to-r from-blue-600 to-violet-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{t('nxa.badge')}</span></div>
        <nav className="flex-1 space-y-0.5 px-3 pt-2">
          {NAV.filter((n) => !n.caps || can(user, ...n.caps)).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => cn('group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors',
              isActive ? 'bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-violet-500/25' : 'text-foreground/75 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-violet-500/10 hover:text-foreground')}>
              <n.icon className="size-4 shrink-0" />{t(`nxa.nav.${n.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t px-4 py-3 text-[11px] text-muted-foreground"><ShieldCheck className="me-1 inline size-3.5 text-emerald-500" />{t('nxa.secure')}<div className="mt-1 font-mono text-[10px] opacity-80">Netxol One {versionLabel()}</div></div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative z-40 flex h-16 shrink-0 items-center gap-3 border-b bg-card/60 px-5 backdrop-blur">
          <div className="text-sm font-semibold text-muted-foreground">{t('nxa.title')}</div>
          <div className="ms-auto flex items-center gap-2">
            <Button variant="ghost" size="icon-sm" title={t('shell.theme')} onClick={() => setDark((d) => !d)}>{dark ? <Sun className="size-4" /> : <Moon className="size-4" />}</Button>
            <UserMenu />
          </div>
        </header>
        <main className="nx-canvas scrollbar-slim flex-1 overflow-y-auto pb-10"><Suspense fallback={<div className="grid place-items-center py-24"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>}><Outlet /></Suspense></main>
      </div>
    </div>
  );
}

function UserMenu() {
  const t = useT(); const nav = useNavigate();
  const { user, clear } = useAdmin();
  const [open, setOpen] = useState(false); const [pw, setPw] = useState(false);
  const logout = async () => { await axios.post('/api/platform/auth/logout', {}).catch(() => undefined); clear(); nav('/login'); };
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2.5 rounded-lg px-2 py-1 hover:bg-muted">
        <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-xs font-bold text-white">{(user?.name ?? '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}</div>
        <div className="hidden text-start leading-tight md:block"><div className="text-[13px] font-semibold">{user?.name}</div><div className="text-[11px] text-muted-foreground">{user?.role}</div></div>
        <ChevronDown className="size-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute end-0 top-12 z-50 w-60 rounded-xl border bg-popover p-1.5 shadow-popover" onMouseLeave={() => setOpen(false)}>
          <div className="flex items-center gap-2 px-2.5 pb-1 pt-1.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground"><Globe className="size-3.5" />{t('language.label')}</div>
          {LANGS.map((l) => (
            <button key={l} className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm hover:bg-muted" onClick={() => setLanguage(l as Lang, { reload: true })}>
              <span className="w-6 text-[10.5px] font-bold uppercase text-muted-foreground">{l}</span>{t(`language.${l}`)}
              {(localStorage.getItem('nx.lang') ?? 'en') === l && <Check className="ms-auto size-4 text-primary" />}
            </button>
          ))}
          <div className="my-1 border-t" />
          <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm hover:bg-muted" onClick={() => { setPw(true); setOpen(false); }}><KeyRound className="size-4" /> {t('shell.changePassword')}</button>
          <button className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-rose-600 hover:bg-rose-500/10" onClick={logout}><LogOut className="size-4" /> {t('shell.signOut')}</button>
        </div>
      )}
      <FormDialog open={pw} onOpenChange={setPw} title={t('shell.passwordDialog.title')} description={t('nxa.pw_rule')} submitLabel={t('shell.passwordDialog.submit')}
        fields={[{ name: 'current', label: t('shell.passwordDialog.current'), type: 'password', required: true, span: 2 }, { name: 'next', label: t('shell.passwordDialog.next'), type: 'password', required: true, span: 2 }]}
        onSubmit={async (v) => { try { await plat.post('/me/password', v); toast.success(t('shell.passwordDialog.done')); setPw(false); } catch (e) { toast.error(apiError(e)); } }} />
    </div>
  );
}

/** Password, then the two-factor code — or, on the first sign-in, setting up the authenticator app. */
function LoginPage() {
  const t = useT(); const nav = useNavigate(); const loc = useLocation();
  const { accessToken, set, ready } = useAdmin();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState(''); const [code, setCode] = useState('');
  const [step, setStep] = useState<'password' | 'code' | 'setup'>('password');
  const [setup, setSetup] = useState<{ stepToken: string; qr?: string; secret?: string } | null>(null);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const next = new URLSearchParams(loc.search).get('next') || '/';
  useEffect(() => { document.title = 'Netxol Admin'; }, []);
  if (!ready) return <div className="flex h-screen items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  if (accessToken) return <Navigate to={next} replace />;
  const lang = { headers: { 'Accept-Language': localStorage.getItem('nx.lang') ?? 'en' } };
  const done = (data: { accessToken: string; expiresAt: number; user: Staff }) => { set(data); nav(next, { replace: true }); };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      if (step === 'setup') { done((await axios.post('/api/platform/auth/totp/enable', { stepToken: setup!.stepToken, code }, lang)).data); return; }
      const { data } = await axios.post('/api/platform/auth/login', { email, password, ...(step === 'code' ? { code } : {}) }, lang);
      if (data.totpRequired) { setStep('code'); setCode(''); return; }
      if (data.totpSetup) {
        const s = (await axios.post('/api/platform/auth/totp/setup', { stepToken: data.stepToken }, lang)).data;
        setSetup({ stepToken: data.stepToken, qr: s.qr, secret: s.secret }); setStep('setup'); setCode(''); return;
      }
      done(data);
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b1530] p-6">
      <div className="absolute -start-40 -top-40 size-[520px] rounded-full bg-blue-600/30 blur-3xl" />
      <div className="absolute -bottom-48 end-0 size-[520px] rounded-full bg-violet-600/25 blur-3xl" />
      <form onSubmit={submit} className="relative w-full max-w-sm space-y-5 rounded-2xl border bg-card p-7 shadow-2xl">
        <div className="flex items-center gap-3">
          <NetxolMark className="h-10" />
          <div><div className="text-lg font-bold">Netxol <span className="text-violet-600">Admin</span></div><div className="text-xs text-muted-foreground">{t('nxa.login.sub')}</div></div>
        </div>
        {step === 'password' && <>
          <label className="block space-y-1.5"><span className="text-xs font-semibold">{t('login.email')}</span><Input type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" /></label>
          <label className="block space-y-1.5"><span className="text-xs font-semibold">{t('login.password')}</span><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" /></label>
        </>}
        {step === 'code' && <p className="text-sm text-muted-foreground">{t('nxa.login.code_help')}</p>}
        {step === 'setup' && setup && (
          <div className="space-y-3">
            <p className="text-sm">{t('nxa.login.setup_help')}</p>
            {setup.qr && <img src={setup.qr} alt={t('nxa.login.qr')} className="mx-auto size-44 rounded-lg border bg-white p-1" />}
            <p className="text-center text-[11px] text-muted-foreground">{t('nxa.login.manual')} <code className="select-all break-all font-mono text-[11px] text-foreground">{setup.secret}</code></p>
          </div>
        )}
        {step !== 'password' && (
          <label className="block space-y-1.5"><span className="text-xs font-semibold">{t('nxa.login.code')}</span>
            <Input inputMode="numeric" autoComplete="one-time-code" autoFocus required pattern="\d{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} className="h-11 text-center font-mono text-lg tracking-[0.4em]" />
          </label>
        )}
        {err && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-600">{err}</div>}
        <Button type="submit" className="h-11 w-full text-sm" disabled={busy}>{busy && <Loader2 className="size-4 animate-spin" />} {step === 'password' ? t('login.submit') : step === 'setup' ? t('nxa.login.enable') : t('nxa.login.verify')}</Button>
        <div className="flex justify-center gap-1 text-[11px] font-bold">
          {LANGS.map((l) => <button key={l} type="button" onClick={() => setLanguage(l as Lang, { reload: true })} className={cn('rounded-md px-1.5 py-0.5 uppercase', (localStorage.getItem('nx.lang') ?? 'en') === l ? 'bg-primary text-white' : 'text-muted-foreground')}>{l}</button>)}
        </div>
        <p className="text-center text-[11px] text-muted-foreground">{t('nxa.login.only_staff')}</p>
        <p className="text-center text-[10.5px] text-muted-foreground/70">Netxol One {versionLabel()}</p>
      </form>
    </div>
  );
}
